/**
 * AerialToggle — admin-only button that flips the cinematic orbit camera
 * (store.aerialView) on/off for streaming.
 *
 * Visibility is gated by `isAdminPubkey` on the connected wallet — the
 * allowlist (`VITE_ADMIN_PUBKEY`) in prod, any connected wallet in DEV. This is
 * a cosmetic, read-only camera, so allowlist membership is enough; no signature
 * unlock (unlike the /ops write surface).
 */
import React from "react";
import { useWallet } from "@solana/wallet-adapter-react";

import { isAdminPubkey } from "../ops/adminAuth";
import { useStore } from "../store";

export function AerialToggle(): JSX.Element {
  const { publicKey } = useWallet();
  const aerial = useStore((s) => s.aerialView);
  const setAerial = useStore((s) => s.setAerialView);

  // Prod: strictly the VITE_ADMIN_PUBKEY allowlist. DEV: always show (so it's
  // testable without connecting/funding a wallet locally). isAdminPubkey already
  // returns true in DEV *when a wallet is connected* — the `|| DEV` covers the
  // no-wallet-connected case so the button is reachable on the dev server.
  const visible = import.meta.env.DEV || isAdminPubkey(publicKey?.toBase58() ?? null);
  if (!visible) return <></>;

  const toggle = (): void => {
    const next = !aerial;
    // Free the cursor when entering the shot so the streamer isn't pointer-
    // locked into the (now-disabled) follow cam. PointerLockBinder also stops
    // re-grabbing lock while aerial is on.
    if (next && document.pointerLockElement) document.exitPointerLock?.();
    setAerial(next);
  };

  return (
    <button
      onClick={toggle}
      style={{ ...btnStyle, ...(aerial ? btnActiveStyle : null) }}
      title="Admin-only cinematic orbit camera for streaming"
    >
      <span aria-hidden>🎥</span> {aerial ? "Exit aerial view" : "Aerial view"}
    </button>
  );
}

const btnStyle: React.CSSProperties = {
  position: "absolute",
  // Stacked below the (variable-height) TokenMcapPanel and the flywheel pill
  // (top:138). zIndex:10 matches the pill so neither hides behind the mcap HUD.
  top: 182,
  left: 12,
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 12px",
  background: "rgba(20,20,30,0.85)",
  border: "1px solid #3a3a48",
  borderRadius: 999,
  color: "#cdd3e0",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.2,
  cursor: "pointer",
  zIndex: 10,
  pointerEvents: "auto",
};

const btnActiveStyle: React.CSSProperties = {
  background: "rgba(80, 40, 120, 0.9)",
  border: "1px solid #a07ad0",
  color: "#f0e8ff",
  boxShadow: "0 0 0 1px rgba(160, 122, 208, 0.3)",
};
